import Image from "next/image";
import {
  MOBILE_TIMELINE_DATA,
  TIMELINE_DATA,
} from "../_constants/about-constants";
import { Tooltip } from "@/v2-components/ui/tooltip";
import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";
import { useState } from "react";

export default function HistorySection() {
  const { t } = useTrans(["about"]);
  return (
    <div className="relative flex flex-col items-start justify-start gap-16 self-stretch">
      <div className="flex flex-col items-center justify-center gap-8 self-stretch">
        <h2 className="justify-start self-stretch text-center text-2xl laptop:text-4xl">
          {t("Our History")}
        </h2>
        <div className="max-w-[708px] justify-start text-center text-base tracking-wide laptop:text-lg">
          {t(
            `Founded in 2015, bHaptics is a global leader in wearable haptics. We design and manufacture full-body haptic devices, that can be used with SteamVR, Meta Quest, PSVR2 and all major VR headsets. In 2025 we officially expanded support to PC Flat-Screen games, with a SimHub Plug-in for Sim Racing and Overwolf integration.`,
          )}
        </div>
      </div>

      <div className="relative hidden w-full laptop:flex">
        <DesktopTimeline />
        <TimelineYearBg />
      </div>
      <div className="relative flex w-full items-center justify-center">
        <MobileTimeline />
      </div>

      <div className="flex items-center justify-center self-stretch">
        <div className="max-w-[708px] justify-start text-center text-base tracking-wide laptop:text-lg">
          {t(
            `Our haptic devices are easy to set up, easy to use, easy to clean and have wireless Bluetooth connectivity. For more than 10 years, we have been improving our devices and ecosystems based on customer feedback. They're a go-to solution for users looking to elevate their digital experiences.`,
          )}
        </div>
      </div>
    </div>
  );
}

interface TimelineYearProps {
  year: string;
  images?: { src: string; alt: string }[];
}

function TimelineYear({ year, images = [] }: TimelineYearProps) {
  return (
    <div className="flex flex-col items-center gap-5">
      <div className="flex min-h-[92px] flex-row items-center">
        {images.map((image, index) => (
          <TimelineItem key={index} image={image} />
        ))}
      </div>
      <div className="flex-1 justify-start rounded bg-coolgray-10 px-[6px] text-center text-base">
        {year}
      </div>
    </div>
  );
}

function TimelineYearBg() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1204"
      height="341"
      viewBox="0 0 1204 341"
      fill="none"
      className="absolute bottom-[3px] z-[-1] w-full"
      preserveAspectRatio="none"
      style={{
        strokeWidth: "4px",
        filter: "drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.15))",
      }}
    >
      <g filter="url(#filter0_d_17702_70182)">
        <path
          d="M2.58579 14.5858C1.80474 15.3668 1.80474 16.6332 2.58579 17.4142L15.3137 30.1421C16.0948 30.9232 17.3611 30.9232 18.1421 30.1421C18.9232 29.3611 18.9232 28.0948 18.1421 27.3137L6.82843 16L18.1421 4.68629C18.9232 3.90524 18.9232 2.63891 18.1421 1.85786C17.3611 1.07682 16.0948 1.07682 15.3137 1.85786L2.58579 14.5858ZM1199.93 135.933L1201.93 135.93L1199.93 135.933ZM1199.8 55.933L1197.8 55.9364L1199.8 55.933ZM4 16V18H1159.8V16V14H4V16ZM1199.8 55.933L1197.8 55.9364L1197.93 135.936L1199.93 135.933L1201.93 135.93L1201.8 55.9297L1199.8 55.933ZM1159.93 176V174H44V176V178H1159.93V176ZM4 216H2V295.5H4H6V216H4ZM44 335.5V337.5H1200V335.5V333.5H44V335.5ZM4 295.5H2C2 318.696 20.804 337.5 44 337.5V335.5V333.5C23.0132 333.5 6 316.487 6 295.5H4ZM44 176V174C20.804 174 2 192.804 2 216H4H6C6 195.013 23.0132 178 44 178V176ZM1199.93 135.933L1197.93 135.936C1197.97 156.948 1180.94 174 1159.93 174V176V178C1183.16 178 1201.97 159.153 1201.93 135.93L1199.93 135.933ZM1159.8 16V18C1180.76 18 1197.76 34.9744 1197.8 55.9364L1199.8 55.933L1201.8 55.9297C1201.76 32.7612 1182.97 14 1159.8 14V16Z"
          fill="#F2F4F7"
        />
      </g>
      <defs>
        <filter
          id="filter0_d_17702_70182"
          x="0"
          y="0.272095"
          width="1203.93"
          height="340.228"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="1" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow_17702_70182"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow_17702_70182"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  );
}

function TimelineItem({ image }: { image: { src: string; alt: string } }) {
  const [hovered, setHovered] = useState(false);

  return (
    <Tooltip
      delayDuration={50}
      tooltipContent={<div className="whitespace-pre-line">{image.alt}</div>}
      align="center"
      portal
    >
      <div
        className="relative transition-all"
        style={{
          width: hovered ? "92px" : "64px",
          height: hovered ? "92px" : "64px",
        }}
      >
        <Image
          onPointerEnter={() => setHovered(true)}
          onPointerLeave={() => setHovered(false)}
          className="absolute left-0 top-0 rounded-lg object-cover transition-transform duration-300"
          src={image.src}
          alt={image.alt}
          fill
        />
      </div>
    </Tooltip>
  );
}

// Timeline Bar Component
function DesktopTimeline() {
  return (
    <div className="grid w-full grid-cols-4 gap-x-6 gap-y-[30px]">
      {TIMELINE_DATA.map((item) => (
        <TimelineYear key={item.year} year={item.year} images={item.images} />
      ))}
    </div>
  );
}

function MobileTimeline() {
  return (
    <div className="relative flex flex-col items-start justify-start gap-6 laptop:hidden">
      <div className="z-[1] my-4 flex w-full flex-col items-start justify-start gap-6">
        {MOBILE_TIMELINE_DATA.toReversed().map((item) => (
          <div key={item.year} className="flex items-start justify-start gap-6">
            <div className="w-[72px] justify-start text-right text-base font-400 text-coolgray-70">
              {item.year}
            </div>

            <div
              className={cn(
                "mt-1 h-2 w-2 rounded-full outline outline-4",
                item.type === "device"
                  ? "bg-blue-60 outline-blue-20"
                  : "bg-bhaptics-yellow outline-yellow-10",
              )}
            />

            <div className="flex flex-col">
              <div className="whitespace-pre-wrap text-base">
                {item.image.alt}
              </div>
              <div className="relative h-16 w-16">
                <Image
                  className="absolute mt-2 rounded-lg object-contain"
                  src={item.image.src}
                  alt={item.image.alt}
                  fill
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="absolute left-[99px] h-full w-px bg-coolgray-20" />
    </div>
  );
}
